import { PayloadAction } from '@reduxjs/toolkit';

import { LOCAL_TABLE_STORAGE_KEYS } from 'app/modules/localTableSettings/constants';
import {
  ColumnMetadata,
  LocalColumnConfig,
  LocalTableConfigState,
  LocalTableConfigType,
} from 'app/modules/localTableSettings/models';
import { buildReconciledColumns } from 'app/modules/localTableSettings/utils';
import { u21CreateSlice } from 'app/shared/thunk/u21CreateSlice';

const readVisibleKeysFromStorage = (
  type: LocalTableConfigType,
): string[] | undefined => {
  try {
    const raw = localStorage.getItem(LOCAL_TABLE_STORAGE_KEYS[type]);
    if (!raw) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')
      ? parsed
      : undefined;
  } catch {
    return undefined;
  }
};

export const LOCAL_TABLE_SETTINGS_SLICE_NAME = 'localTableSettings';

export const localTableSettingsSlice = u21CreateSlice({
  name: LOCAL_TABLE_SETTINGS_SLICE_NAME,
  initialState: {} satisfies LocalTableConfigState,
  reducers: {
    syncLocalTableConfig: (
      draft,
      action: PayloadAction<{
        type: LocalTableConfigType;
        columns: ColumnMetadata[];
        defaultVisibleKeys: string[];
      }>,
    ) => {
      const { type, columns, defaultVisibleKeys } = action.payload;
      const existing = draft[type];
      // `existing` is truthy-but-empty when a prior sync ran before `columns`
      // was available. In that case, fall back to storage/defaults so we seed
      // correctly once real columns arrive.
      const visibleKeys =
        existing && existing.length > 0
          ? existing.filter((c) => !c.hidden).map((c) => c.key)
          : (readVisibleKeysFromStorage(type) ?? defaultVisibleKeys);
      draft[type] = buildReconciledColumns(columns, visibleKeys);
    },
    setLocalTableConfig: (
      draft,
      action: PayloadAction<{
        type: LocalTableConfigType;
        columns: LocalColumnConfig[];
      }>,
    ) => {
      draft[action.payload.type] = action.payload.columns;
    },
  },
});

export const { syncLocalTableConfig, setLocalTableConfig } =
  localTableSettingsSlice.actions;

export default localTableSettingsSlice.reducer;
